/**
 * Where a sign-in may send the browser, as pure string rules: no Node or
 * browser APIs, so the server routes and the pages share them.
 */

/** What sign-in asks the orchestrator for. */
export const SCOPE = 'openid profile email offline_access'

/** Where the orchestrator sends the browser back to, on this origin. */
export const CALLBACK_PATH = '/auth/callback'

/**
 * What a refused sign-in says to the person, from the identity provider's
 * `error` and `error_description`.
 */
export function signInRefusal(
  error: string,
  description?: string | null
): string {
  if (description === 'email_not_verified') {
    return 'Verify your email address with the link we sent you, then sign in again.'
  }

  return `The identity provider refused the sign-in: ${hint(description) ?? hint(error) ?? 'unknown error'}`
}

/**
 * A short, printable passthrough value (an org id, a `login_hint`), or
 * undefined for anything else.
 */
export function hint(value: unknown): string | undefined {
  if (
    typeof value !== 'string' ||
    !value ||
    value.length > 256 ||
    /[\u0000-\u001f]/.test(value)
  ) {
    return undefined
  }

  return value
}

/**
 * Where a sign-in may send the browser afterwards: a path on this origin,
 * else `/`. Never another origin, a protocol-relative URL or a backslash
 * trick.
 */
export function safeReturnTo(value: unknown): string {
  if (
    typeof value !== 'string' ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.startsWith('/\\') ||
    /[\u0000-\u001f\\]/.test(value)
  ) {
    return '/'
  }

  return value
}

// The pages a person with no org yet may land on after signing in: each
// gives them an org, or needs none (Zendesk).
const ORGLESS_DESTINATIONS = ['/accept-invite', '/callback/azure', '/support']

/**
 * Where the browser goes once signed in: `/onboarding` for a session with no
 * org (a fresh sign-up), unless it is headed somewhere that gives it one;
 * otherwise `returnTo` (`/explore` for `/`), marked `#fromLogin`.
 */
export function destinationAfterLogin({
  returnTo,
  hasOrg,
}: {
  returnTo: string
  hasOrg: boolean
}): string {
  const path = safeReturnTo(returnTo)
  const pathname = path.split(/[?#]/)[0]

  if (
    !hasOrg &&
    !ORGLESS_DESTINATIONS.some(
      (page) => pathname === page || pathname.startsWith(`${page}/`)
    )
  ) {
    return '/onboarding'
  }

  const target = pathname === '/' ? '/explore' : path

  return `${target.split('#')[0]}#fromLogin`
}

// Zendesk's remote-login URL sends the browser to `/login?return_to=` on
// one of these hosts.
const ZENDESK_HOSTS = ['support.mapped.com', 'mapped.zendesk.com']

/**
 * Where the old sign-in pages (`/login`, `/signup`, `/forgot-password`)
 * send the browser now: the SSO orchestrator's universal login, which does
 * all three. Their `return_to` is kept — a path on this origin as is, and
 * Zendesk's remote login through `/support`, which signs the visitor in to
 * Zendesk once they are signed in here.
 */
export function legacyLoginTarget(returnTo: unknown): string {
  const value = typeof returnTo === 'string' ? returnTo : ''

  let next: string | undefined

  try {
    const url = new URL(value)

    if (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      ZENDESK_HOSTS.includes(url.hostname)
    ) {
      next = `/support?${new URLSearchParams({ return_to: value }).toString()}`
    }
  } catch {
    next = safeReturnTo(value) === value ? value : undefined
  }

  return next
    ? `/auth/login?${new URLSearchParams({ returnTo: next }).toString()}`
    : '/auth/login'
}
